import * as React from 'react';
import cx from 'clsx';
import { TextWithHighlights } from '../price-plan/text-with-highlights.tsx';
import { BenefitsGroup } from '../price-plan/benefits-group.tsx';
import type { AddonsGroupProps } from '../price-plan/addons-group.tsx';
import { AddonsGroup } from '../price-plan/addons-group.tsx';
import { Divider } from '../divider.tsx';
import styles from '../price-plan/price-plan.module.css';

export interface CustomPricePlanProps {
  addons: AddonsGroupProps;
  mainButton: React.ReactNode;
  benefitsGroup: BenefitsGroupInterface[];
  ctaDescription: string;
  ctaName: string;
  description: string;
  price: string;
  title: string;
  compact?: boolean;
  hasPromoInAnyProducts?: boolean;
  hidePromoInAllProducts?: boolean;
  isOnboardingVariant?: boolean;
}

interface BenefitsGroupInterface {
  title: string;
  benefits: Benefit[];
}

interface Benefit {
  description: string;
  isIncluded: boolean;
}

export function CustomPricePlan({
  addons,
  mainButton,
  benefitsGroup,
  compact,
  ctaDescription,
  description,
  price,
  title,
  hasPromoInAnyProducts,
  isOnboardingVariant,
}: CustomPricePlanProps): React.ReactNode {
  const shouldHideTopPadding = isOnboardingVariant && !hasPromoInAnyProducts;

  return (
    <div className={cx(styles.container, compact && styles.compact)}>
      <div className={styles.cardContainer}>
        <div className={styles.titleContainer}>
          <h2 className={cx(styles.title, 'heading-section')}>{title}</h2>
        </div>
        <div className={styles.priceSection}>
          <span className="heading-section">{price}</span>
        </div>
        {!compact ? (
          <p className={cx(styles.description, 'body-standard')}>
            <TextWithHighlights isHighlighted={false}>{description}</TextWithHighlights>
          </p>
        ) : null}
        <div className={styles.ctaContainer}>
          {mainButton}
          <p className={cx(styles.ctaDescription, 'body-standard')}>{ctaDescription}</p>
        </div>
        {compact ? (
          <ul className={styles.compactBenefitsList} data-test-compact-benefits>
            {(benefitsGroup[0]?.benefits.filter(b => b.isIncluded) ?? []).map(benefit => (
              <li
                className={cx(styles.compactBenefitItem, 'body-standard')}
                key={benefit.description}
              >
                <TextWithHighlights isHighlighted={false}>{benefit.description}</TextWithHighlights>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div
        className={cx(
          styles.benefitsContainer,
          shouldHideTopPadding && styles.benefitsContainerWithoutTopPadding
        )}
      >
        {!compact ? (
          <>
            <div className={styles.benefitsGroups}>
              {benefitsGroup.map(({ title: groupTitle, benefits }) => (
                <BenefitsGroup benefits={benefits} key={groupTitle} title={groupTitle} />
              ))}
            </div>
            <div className={styles.bottomSection}>
              <Divider />
              <AddonsGroup addons={addons.addons} title={addons.title} />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
